/**
 * useDarkMode
 *
 * Manages the dark/light mode toggle.
 *
 * How it works:
 *  - Tailwind uses `darkMode: 'selector'` in tailwind.config.js, which activates
 *    `dark:` utilities when the `.dark` class is on <html>.
 *  - PrimeVue Aura is configured with `darkModeSelector: '.dark'` in main.js,
 *    so it follows the same class.
 *  - The preference is persisted to localStorage under the key 'color-scheme'.
 *
 * Usage:
 *   const { isDark, toggle, setDark } = useDarkMode()
 */

import { ref, watch } from 'vue'

const STORAGE_KEY = 'color-scheme'

// Module-level ref so all consumers share the same state
const isDark = ref(false)

function applyClass(dark) {
  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}

function init() {
  const stored = localStorage.getItem(STORAGE_KEY)
  isDark.value = stored === 'dark'
  applyClass(isDark.value)
}

watch(isDark, (val) => {
  applyClass(val)
  localStorage.setItem(STORAGE_KEY, val ? 'dark' : 'light')
})

export function useDarkMode() {
  return {
    isDark,
    toggle() {
      isDark.value = !isDark.value
    },
    setDark(val) {
      isDark.value = val
    },
    init,
  }
}
